import type { Locale } from "@ec2/i18n";

/** `17:00` in the event's timezone (participants are at the venue). */
export function formatClock(date: Date, locale: Locale, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(date);
  }
}

/** `in 3 hr` / `vor 5 Min.` — largest sensible unit, via Intl.RelativeTimeFormat. */
export function formatRelative(target: Date, locale: Locale, now: Date = new Date()): string {
  const diffMs = target.getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always", style: "narrow" });
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, "minute");
  const hours = Math.round(diffMs / 3_600_000);
  if (Math.abs(hours) < 48) return rtf.format(hours, "hour");
  const days = Math.round(diffMs / 86_400_000);
  return rtf.format(days, "day");
}

/** `Di., 21. Okt., 08:00` — an event's start, in its own timezone. */
export function formatEventStart(date: Date, locale: Locale, timeZone?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat(locale, { ...opts, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, opts).format(date);
  }
}

/** Countdown parts until `target` (never negative). */
export function countdownParts(target: Date, now: Date = new Date()) {
  const total = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
  return {
    total,
    days: Math.floor(total / 86_400),
    hours: Math.floor((total % 86_400) / 3_600),
    minutes: Math.floor((total % 3_600) / 60),
    seconds: total % 60,
  };
}
